import { enumMembers, type ApiSchema } from "../apiSchema";
import { VALUE_KINDS, type ValueKind } from "../content/authoring/selectorDecoders";
import { readPreparedWorkflow, type PreparedWorkflow } from "../content/stepPlans";
import { jsonBody, requestJson } from "../http/httpClient";
import { readArray, readInteger, readNullableString, readObject, readOneOf, readString, type JsonObject } from "../http/jsonDecoding";
import { readProjectedValue, readThreadEntry, type ProjectedValue, type ThreadEntry } from "./liveMessages";
import { threadPath } from "./threadsApi";

/**
 * Switching a Thread to another Workflow at any step, fed by the outputs its steps made so
 * far (F126; ADR-0040, built provisionally, awaiting acceptance). The server decides what is
 * compatible, what each Workflow carries and what it still misses; the web app renders it.
 */

export type SwitchRefusal = ApiSchema<"SwitchRefusal">;
const SWITCH_REFUSALS = enumMembers<SwitchRefusal>({ batch_item: true });

/** An output the Thread's steps made so far. */
export interface MadeOutput {
  /** The entry that holds it: where "show" scrolls to. */
  readonly entry: number;
  readonly part: string;
  /** The step that made it, a key of `workflow`. */
  readonly stepKey: string;
  /** That step's name; `null` when its version no longer reads. */
  readonly stepName: string | null;
  /** The Workflow version the step ran under. */
  readonly workflow: string;
  readonly kind: ValueKind;
  /** What it gave, at a glance, as module C sums it up (WD-4). */
  readonly value: ProjectedValue;
}

/** An input an output made so far fills: by index into `made`. */
export interface CarriedInput {
  readonly destination: string;
  readonly output: number;
  /** Every output the input takes, newest first. */
  readonly candidates: readonly number[];
}

export interface SwitchWorkflow extends PreparedWorkflow {
  readonly carried: readonly CarriedInput[];
}

export interface WorkflowSwitchOptions {
  /** Oldest first. */
  readonly made: readonly MadeOutput[];
  /** Why no Workflow can start on this Thread; `null` when one can. */
  readonly refused: SwitchRefusal | null;
  /** The compatible Workflows, without the one followed now; or the one asked for. */
  readonly workflows: readonly SwitchWorkflow[];
}

/** An output made so far, named for the server. */
export interface OutputChoice {
  readonly entry: number;
  readonly part: string;
}

export interface SwitchRequest {
  /** The Workflow version to prepare; `null` lists every compatible one. */
  readonly workflow: string | null;
  /** The person's choice of output for an input, by destination. */
  readonly carried: Readonly<Record<string, OutputChoice>>;
  /** Values for the Workflow's inputs, by name or destination, as a start's. */
  readonly inputValues: Readonly<Record<string, unknown>>;
}

/** What the Thread made so far and the Workflows that can start on it with that. Writes nothing. */
export function readWorkflowSwitch(threadId: string, request: SwitchRequest, signal?: AbortSignal): Promise<WorkflowSwitchOptions> {
  return requestJson({
    method: "POST",
    path: threadPath(threadId, "/workflow-switch"),
    body: jsonBody({ workflow: request.workflow, carried: request.carried, input_values: request.inputValues } satisfies ApiSchema<"WorkflowSwitchBody">),
    signal,
    decodeResponse: (body) => decodeSwitchOptions(readObject(body, "Workflow switch")),
  });
}

/** Start `workflow` on the Thread, leaving the one it follows (host only). */
export function switchWorkflow(threadId: string, request: SwitchRequest & { readonly workflow: string; readonly expectedLastEntryNumber: number }): Promise<ThreadEntry> {
  const body = {
    event: "started",
    step_key: null,
    workflow: request.workflow,
    input_values: request.inputValues,
    carried: request.carried,
    expected_last_entry_number: request.expectedLastEntryNumber,
  } satisfies ApiSchema<"WorkflowActionBody">;
  return requestJson({ method: "POST", path: threadPath(threadId, "/workflow-actions"), body: jsonBody(body), decodeResponse: readThreadEntry });
}

export function decodeSwitchOptions(object: JsonObject): WorkflowSwitchOptions {
  return {
    made: readArray(object["made"], "outputs made so far", (item) => {
      const made = readObject(item, "output made so far");
      return {
        entry: readInteger(made, "entry"),
        part: readString(made, "part"),
        stepKey: readString(made, "step_key"),
        stepName: readNullableString(made, "step_name"),
        workflow: readString(made, "workflow"),
        kind: readOneOf(made, "kind", VALUE_KINDS),
        value: readProjectedValue(made["value"]),
      };
    }),
    refused: object["refused"] === null || object["refused"] === undefined ? null : readOneOf(object, "refused", SWITCH_REFUSALS),
    workflows: readArray(object["workflows"], "switch Workflows", (item) => {
      const workflow = readObject(item, "switch Workflow");
      return {
        ...readPreparedWorkflow(workflow),
        carried: readArray(workflow["carried"], "carried inputs", (entry) => {
          const carried = readObject(entry, "carried input");
          return {
            destination: readString(carried, "destination"),
            output: readInteger(carried, "output"),
            candidates: readArray(carried["candidates"], "candidates", (index) => readInteger({ index }, "index")),
          };
        }),
      };
    }),
  };
}
